// src/components/Lessons.js
import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  doc,
} from "firebase/firestore";
import { db } from "../firebase";
import PageHeader from "./ui/PageHeader";
import Button from "./ui/Button";
import Badge from "./ui/Badge";
import Modal from "./ui/Modal";
import { TextField, SelectField } from "./ui/Field";
import { TableSkeleton } from "./ui/Skeleton";

// 料金項目（calc01〜17）の定義。ラベルはレッスンごとの calcNN_name を優先し、なければ既定値。
const CALC_FIELDS = [
  { n: 1, label: "固定級" },
  { n: 2, label: "週1プラン" },
  { n: 3, label: "週2プラン" },
  { n: 4, label: "フリーパス" },
  { n: 5, label: "全校フリーパス" },
  { n: 6, label: "週1プラン(学割)" },
  { n: 7, label: "週2プラン(学割)" },
  { n: 8, label: "フリーパス(学割)" },
  { n: 9, label: "全校フリーパス(学割)" },
  { n: 10, label: "家族割週1" },
  { n: 11, label: "日割1回（週1）" },
  { n: 12, label: "日割1回（週2）" },
  { n: 13, label: "日割1回（学割週1）" },
  { n: 14, label: "日割1回（学割週2）" },
  { n: 15, label: "日割1回（家族割週1）" },
  { n: 16, label: "ビジター1回" },
  { n: 17, label: "体験" },
];

const DAYS = ["月", "火", "水", "木", "金", "土", "日"];
const SALARY_TYPES = ["A", "B", "C", "D", "E"];

const key = (n, suffix) => `calc${String(n).padStart(2, "0")}_${suffix}`;

// 給料形態ごとのデフォルト料金
const salaryTypeDefaults = {
  A: { 1: 0, 2: 300, 3: 300, 4: 200, 5: 200, 6: 300, 7: 300, 8: 200, 9: 200, 10: 300, 11: 300, 12: 300, 13: 300, 14: 300, 15: 300, 16: 300, 17: 0 },
  B: { 1: 0, 2: 2080, 3: 1600, 4: 1000, 5: 800, 6: 1600, 7: 1400, 8: 1000, 9: 800, 10: 1600, 11: 520, 12: 400, 13: 400, 14: 350, 15: 400, 16: 720, 17: 0 },
  C: { 1: 3500, 2: 300, 3: 300, 4: 200, 5: 200, 6: 300, 7: 300, 8: 200, 9: 200, 10: 300, 11: 300, 12: 300, 13: 300, 14: 300, 15: 300, 16: 300, 17: 0 },
  D: { 1: 0, 2: 300, 3: 300, 4: 200, 5: 200, 6: 300, 7: 300, 8: 200, 9: 200, 10: 300, 11: 300, 12: 300, 13: 300, 14: 300, 15: 300, 16: 300, 17: 0 },
  E: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0, 11: 0, 12: 0, 13: 0, 14: 0, 15: 0, 16: 0, 17: 0 },
};

// salary_type に対応する price 群を {calcNN_price: value} に展開
const pricesForType = (type) => {
  const table = salaryTypeDefaults[type] || salaryTypeDefaults.A;
  return CALC_FIELDS.reduce(
    (acc, f) => ({ ...acc, [key(f.n, "price")]: table[f.n] }),
    {}
  );
};

// 既定のラベル群 {calcNN_name: label}
const defaultNames = CALC_FIELDS.reduce(
  (acc, f) => ({ ...acc, [key(f.n, "name")]: f.label }),
  {}
);

const emptyLesson = () => ({
  name: "",
  time: "",
  day: "",
  studio_id: "",
  instructor_id: "",
  instructor_name: "",
  salary_type: "A",
  del_flg: false,
  ...defaultNames,
  ...pricesForType("A"),
});

function Lessons() {
  const [lessons, setLessons] = useState([]);
  const [studios, setStudios] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [currentLesson, setCurrentLesson] = useState(emptyLesson());
  const [isEditing, setIsEditing] = useState(false);
  const [selectedStudioId, setSelectedStudioId] = useState("all");

  useEffect(() => {
    fetchLessons();
    fetchStudios();
    fetchInstructors();
  }, []);

  const fetchLessons = async () => {
    try {
      setLoading(true);
      const lessonsSnapshot = await getDocs(collection(db, "lessons2"));
      const lessonsList = lessonsSnapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((lesson) => !lesson.del_flg);
      setLessons(lessonsList);
    } catch (error) {
      console.error("Error fetching lessons: ", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudios = async () => {
    try {
      const studiosSnapshot = await getDocs(collection(db, "studios"));
      setStudios(
        studiosSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
      );
    } catch (error) {
      console.error("Error fetching studios: ", error);
    }
  };

  const fetchInstructors = async () => {
    try {
      const instructorsSnapshot = await getDocs(collection(db, "instructors"));
      setInstructors(
        instructorsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
      );
    } catch (error) {
      console.error("Error fetching instructors: ", error);
    }
  };

  const handleAddEdit = async () => {
    try {
      const instructor = instructors.find(
        (i) => i.id === currentLesson.instructor_id
      );
      const lessonData = {
        ...currentLesson,
        instructor_name: instructor ? instructor.name : "",
      };

      if (isEditing) {
        await updateDoc(doc(db, "lessons2", currentLesson.id), lessonData);
      } else {
        await addDoc(collection(db, "lessons2"), lessonData);
      }
      setShowModal(false);
      fetchLessons();
    } catch (error) {
      console.error("Error saving lesson: ", error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("本当に削除しますか？")) {
      try {
        await updateDoc(doc(db, "lessons2", id), { del_flg: true });
        fetchLessons();
      } catch (error) {
        console.error("Error deleting lesson: ", error);
      }
    }
  };

  const handleSalaryTypeChange = (e) => {
    const newSalaryType = e.target.value;
    setCurrentLesson({
      ...currentLesson,
      salary_type: newSalaryType,
      ...pricesForType(newSalaryType),
    });
  };

  const openAddModal = () => {
    const visibleStudios = studios.filter((s) => s.display_flg);
    const initialStudioId =
      selectedStudioId !== "all"
        ? selectedStudioId
        : visibleStudios.length > 0
        ? visibleStudios[0].id
        : studios.length > 0
        ? studios[0].id
        : "";

    setCurrentLesson({
      ...emptyLesson(),
      studio_id: initialStudioId,
      instructor_id: instructors.length > 0 ? instructors[0].id : "",
    });
    setIsEditing(false);
    setShowModal(true);
  };

  const openEditModal = (lesson) => {
    setCurrentLesson(lesson);
    setIsEditing(true);
    setShowModal(true);
  };

  const visibleStudios = studios.filter(
    (studio) => selectedStudioId === "all" || studio.id === selectedStudioId
  );
  const hasAnyLesson = visibleStudios.some((studio) =>
    lessons.some((lesson) => lesson.studio_id === studio.id)
  );

  return (
    <div>
      <PageHeader
        title="レッスン管理"
        subtitle={loading ? undefined : `全${lessons.length}件`}
        actions={
          <Button variant="primary" onClick={openAddModal}>
            <i className="ti ti-plus" aria-hidden="true" />
            新規追加
          </Button>
        }
      />

      {/* スタジオフィルター */}
      <div className="row-wrap" style={{ marginBottom: 24 }}>
        <button
          type="button"
          className={`chip ${selectedStudioId === "all" ? "chip--active" : ""}`}
          onClick={() => setSelectedStudioId("all")}
        >
          すべて
        </button>
        {studios
          .filter((studio) => studio.display_flg)
          .map((studio) => (
            <button
              key={studio.id}
              type="button"
              className={`chip ${
                selectedStudioId === studio.id ? "chip--active" : ""
              }`}
              onClick={() => setSelectedStudioId(studio.id)}
            >
              {studio.name}
            </button>
          ))}
      </div>

      {loading ? (
        <TableSkeleton rows={6} columns={5} />
      ) : !hasAnyLesson ? (
        <div className="card empty">
          <i className="ti ti-calendar-off empty__icon" aria-hidden="true" />
          <p style={{ margin: 0 }}>表示できるレッスンがありません</p>
          <Button variant="primary" size="sm" onClick={openAddModal}>
            <i className="ti ti-plus" aria-hidden="true" />
            レッスンを追加
          </Button>
        </div>
      ) : (
        visibleStudios.map((studio) => {
          const studioLessons = lessons.filter(
            (lesson) => lesson.studio_id === studio.id
          );
          if (studioLessons.length === 0) return null;

          return (
            <section key={studio.id} style={{ marginBottom: 36 }}>
              <h2
                style={{
                  fontSize: 16,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 12,
                }}
              >
                <i
                  className="ti ti-building"
                  aria-hidden="true"
                  style={{ color: "var(--text-tertiary)", fontSize: 18 }}
                />
                {studio.name}
              </h2>

              {DAYS.map((day) => {
                const dayLessons = studioLessons
                  .filter((lesson) => lesson.day === day)
                  .sort((a, b) => (a.time || "").localeCompare(b.time || ""));
                if (dayLessons.length === 0) return null;

                return (
                  <div key={day} style={{ marginBottom: 16 }}>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 500,
                        color: "var(--text-tertiary)",
                        padding: "0 2px 6px",
                      }}
                    >
                      {day}曜日
                    </div>
                    <div className="card" style={{ overflow: "hidden" }}>
                      <table className="table">
                        <thead>
                          <tr>
                            <th style={{ width: 130 }}>時間</th>
                            <th>レッスン名</th>
                            <th style={{ width: 150 }}>インストラクター</th>
                            <th style={{ width: 90 }}>給与</th>
                            <th style={{ width: 90 }} className="cell-actions">
                              操作
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {dayLessons.map((lesson) => (
                            <tr key={lesson.id}>
                              <td
                                className="num"
                                style={{ color: "var(--text-secondary)" }}
                              >
                                {lesson.time}
                              </td>
                              <td style={{ fontWeight: 500 }}>{lesson.name}</td>
                              <td style={{ color: "var(--text-secondary)" }}>
                                {lesson.instructor_name}
                              </td>
                              <td>
                                <Badge tone="neutral">
                                  {lesson.salary_type}
                                </Badge>
                              </td>
                              <td className="cell-actions">
                                <button
                                  type="button"
                                  className="icon-btn"
                                  aria-label="編集"
                                  title="編集"
                                  onClick={() => openEditModal(lesson)}
                                >
                                  <i className="ti ti-edit" aria-hidden="true" />
                                </button>
                                <button
                                  type="button"
                                  className="icon-btn"
                                  aria-label="削除"
                                  title="削除"
                                  onClick={() => handleDelete(lesson.id)}
                                  style={{ color: "var(--danger)" }}
                                >
                                  <i
                                    className="ti ti-trash"
                                    aria-hidden="true"
                                  />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </section>
          );
        })
      )}

      {/* 追加・編集モーダル */}
      <Modal
        show={showModal}
        onClose={() => setShowModal(false)}
        size="lg"
        title={isEditing ? "レッスンを編集" : "新規レッスン追加"}
        footer={
          <>
            <Button onClick={() => setShowModal(false)}>キャンセル</Button>
            <Button variant="primary" onClick={handleAddEdit}>
              保存
            </Button>
          </>
        }
      >
        <h3 style={{ fontSize: 14, marginBottom: 12 }}>基本情報</h3>

        <SelectField
          label="スタジオ"
          value={currentLesson.studio_id}
          onChange={(e) =>
            setCurrentLesson({ ...currentLesson, studio_id: e.target.value })
          }
        >
          {studios
            .filter((studio) => studio.display_flg)
            .map((studio) => (
              <option key={studio.id} value={studio.id}>
                {studio.name}
              </option>
            ))}
        </SelectField>

        <TextField
          label="レッスン名"
          value={currentLesson.name}
          onChange={(e) =>
            setCurrentLesson({ ...currentLesson, name: e.target.value })
          }
        />

        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <SelectField
              label="曜日"
              value={currentLesson.day}
              onChange={(e) =>
                setCurrentLesson({ ...currentLesson, day: e.target.value })
              }
            >
              <option value="">選択してください</option>
              {DAYS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </SelectField>
          </div>
          <div style={{ flex: 1 }}>
            <TextField
              label="時間"
              value={currentLesson.time}
              onChange={(e) =>
                setCurrentLesson({ ...currentLesson, time: e.target.value })
              }
              placeholder="例: 18:00〜19:00"
            />
          </div>
        </div>

        <SelectField
          label="インストラクター"
          value={currentLesson.instructor_id}
          onChange={(e) =>
            setCurrentLesson({
              ...currentLesson,
              instructor_id: e.target.value,
            })
          }
        >
          {[...instructors]
            .sort((a, b) => (a.name || "").localeCompare(b.name || "", "ja"))
            .map((instructor) => (
              <option key={instructor.id} value={instructor.id}>
                {instructor.name}
              </option>
            ))}
        </SelectField>

        <SelectField
          label="給与タイプ"
          value={currentLesson.salary_type}
          onChange={handleSalaryTypeChange}
        >
          {SALARY_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </SelectField>

        <h3 style={{ fontSize: 14, margin: "20px 0 12px" }}>料金設定</h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "0 16px",
          }}
        >
          {CALC_FIELDS.map((f) => (
            <TextField
              key={f.n}
              label={currentLesson[key(f.n, "name")] || f.label}
              type="number"
              value={currentLesson[key(f.n, "price")]}
              onChange={(e) =>
                setCurrentLesson({
                  ...currentLesson,
                  [key(f.n, "price")]: Number(e.target.value),
                })
              }
            />
          ))}
        </div>
      </Modal>
    </div>
  );
}

export default Lessons;
